<!-- @format -->
<script setup lang="ts">
/**
 * Shared editorial-split hero (Direction B). Presentational + surface-agnostic:
 * a matboard text panel beside a full-bleed photograph, so the copy is always
 * legible without per-image tuning. Used on the homepage (homepage_hero payload)
 * and, later, collection pages (title/description + collection banner) — the
 * experiment resolution + exposure stays with the caller, keeping the surface a
 * caller concern so collection-specific experiments stay a routing decision.
 *
 * The WHOLE hero is one link (the CTA is a decorative pill, not a nested <a> —
 * that would be invalid HTML and a confusing second tab stop for AT). With no
 * `link` it renders as a static <div> instead — a hero must never link to the
 * page it's already on (collection pages; a homepage payload without a link).
 */
import { RouterLink } from "vue-router";

defineProps<{
  /** Headline — the one required piece of copy. */
  title: string;
  /** Fully-resolved image src (caller prefixes the media path). */
  image: string;
  /** Small kicker label above the headline (category/collection). Optional. */
  eyebrow?: string;
  /** Supporting line under the headline. Optional. */
  sub?: string;
  /** CTA pill label. Optional — omit for a text-only hero. */
  cta?: string;
  /** Destination for the whole hero. Omit to render a static (non-link) hero. */
  link?: string;
  /** Image alt text. Falls back to the title. */
  alt?: string;
}>();
</script>

<template>
  <component
    :is="link ? RouterLink : 'div'"
    :to="link || undefined"
    class="hero"
    :aria-label="link ? (cta ? `${cta}: ${title}` : title) : undefined"
  >
    <div class="hero__panel">
      <p v-if="eyebrow" class="hero__eyebrow">{{ eyebrow }}</p>
      <h1 class="hero__title">{{ title }}</h1>
      <p v-if="sub" class="hero__sub">{{ sub }}</p>
      <span v-if="cta" class="hero__cta">{{ cta }}</span>
    </div>
    <div class="hero__photo">
      <img :src="image" :alt="alt || title" class="hero__img" />
    </div>
  </component>
</template>

<style scoped>
/** @format */

.hero {
  /* Gallery neutrals + a real-wood (walnut) accent from the frames. */
  --paper: #efece5;
  --paper-edge: #e4e0d6;
  --ink: #211d17;
  --muted: #6b655c;
  --walnut: #6f5334;
  --walnut-hover: #5a4229;

  display: grid;
  grid-template-columns: 43% 57%;
  min-height: 440px;
  background: var(--paper);
  color: inherit;
  text-decoration: none;
  overflow: hidden;
}

.hero__panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 52px 48px;
  color: var(--ink);
  border-right: 1px solid var(--paper-edge);
}

.hero__eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.17em;
  text-transform: uppercase;
  color: var(--walnut);
}

.hero__title {
  margin: 14px 0 0;
  font-family: "Iowan Old Style", "Palatino Linotype", Palatino, "Hoefler Text",
    Georgia, serif;
  font-weight: 400;
  font-size: 44px;
  line-height: 1.05;
  letter-spacing: -0.012em;
  color: var(--ink);
  max-width: 13ch;
  text-wrap: balance;
}

.hero__sub {
  margin: 16px 0 0;
  font-size: 16px;
  line-height: 1.6;
  color: var(--muted);
  max-width: 38ch;
}

.hero__cta {
  align-self: flex-start;
  margin-top: 26px;
  padding: 13px 22px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #fff;
  background: var(--walnut);
  border-radius: 2px;
  transition: background-color 0.25s ease;
}

.hero__photo {
  position: relative;
  overflow: hidden;
}

.hero__img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 440px;
  object-fit: cover;
  object-position: center;
  transition: transform 0.6s ease;
}

.hero:hover .hero__cta {
  background: var(--walnut-hover);
}

.hero:hover .hero__img {
  transform: scale(1.02);
}

.hero:focus-visible {
  outline: 2px solid var(--walnut);
  outline-offset: 3px;
}

/* Phone: stack — photograph on top, panel below. */
@media (max-width: 767.98px) {
  .hero {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .hero__photo {
    order: -1;
  }

  .hero__img {
    min-height: 0;
    height: 200px;
  }

  .hero__panel {
    padding: 24px 22px 28px;
    border-right: 0;
    border-bottom: 1px solid var(--paper-edge);
  }

  .hero__title {
    font-size: 26px;
    margin-top: 8px;
  }

  .hero__sub {
    font-size: 14px;
    margin-top: 12px;
    max-width: none;
  }

  .hero__cta {
    margin-top: 18px;
    padding: 11px 18px;
    font-size: 13px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero__img,
  .hero__cta {
    transition: none;
  }

  .hero:hover .hero__img {
    transform: none;
  }
}
</style>
